import React, { useMemo } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { TFunction } from "i18next/typescript/t";
import { DateTime } from "luxon";

import FilterRenderer from "@/components_v2/Filters/FilterRenderer";
import { FilterField } from "@/components_v2/Filters/types";
import {
  HookFormDateRangePicker,
  HookFormInput,
} from "@/components_v2/FormFields/ReactHookForm/fields";
import {
  CommentFiltersProps,
  CommentFiltersState,
} from "@/domains/community/types";

import "../styles.css";

const commentFiltersSchema = (
  t: TFunction,
): yup.ObjectSchema<Omit<CommentFiltersState, "createdBy">> =>
  yup
    .object()
    .shape({
      search: yup
        .string()
        .transform((value) => (value === "" ? undefined : value))
        .typeError(t("form.validation.notValid")),
      createdRange: yup
        .array()
        .of(
          yup
            .mixed<DateTime>()
            .typeError(t("form.validation.notValid"))
            .nullable(),
        )
        .nullable(),
    })
    .required();

const DiscussionFilters = ({
  filterNames,
  setFilters,
  defaultFilters,
}: CommentFiltersProps) => {
  const { t } = useTranslation();

  const form = useForm<CommentFiltersState>({
    resolver: yupResolver(commentFiltersSchema(t)),
    mode: "onChange",
    defaultValues: {
      search: undefined,
      createdRange: [],
      ...defaultFilters,
    },
  });

  const filterFields: Record<string, FilterField> = useMemo(() => {
    return {
      search: {
        name: "search",
        isAdvancedFilter: false,
        isDebounced: true,
        component: (
          <HookFormInput
            name="search"
            control={form.control}
            suffix={<SearchOutlined />}
            placeholder={t("community.placeholders.searchComments")}
          />
        ),
      },
      createdRange: {
        name: "createdRange",
        label: t("community.labels.createdRange"),
        isAdvancedFilter: true,
        isDebounced: false,
        component: (
          <HookFormDateRangePicker
            name="createdRange"
            control={form.control}
            allowEmpty={[true, true]}
          />
        ),
      },
    };
  }, []);

  return (
    <FilterRenderer
      form={form}
      filters={filterFields}
      activeFilterNames={filterNames}
      popoverTitle={t("community.labels.filters")}
      handleClear={() => {
        form.reset(defaultFilters);
        setFilters(form.getValues());
      }}
      onFiltersChange={setFilters}
      areFiltersDirty={form.formState.isDirty}
      handleSearch={() => setFilters(form.getValues())}
      className="filter-layout"
    />
  );
};

export default DiscussionFilters;
